import { render as toPlainText } from 'datocms-structured-text-to-plain-text'
import {
  isEmptyDocument,
  isHeading,
  isLink,
  type StructuredText as StructuredTextType
} from 'datocms-structured-text-utils'
import React from 'react'
import { renderNodeRule, StructuredText } from 'react-datocms'

import Cta from '@/components/blocks-library/cta/cta'
import Embed from '@/components/blocks-library/embed/embed'
import FormBlock from '@/components/blocks-library/form/form'
import Button from '@/components/button/button'
import CardInline, { CardsInline } from '@/components/card/card-inline'
import ImageInline from '@/components/image/image-inline'
import Link from '@/components/link/link'
import Player from '@/components/video/player/player'
import formTypes from '@/constants/form'
import { generateUrl } from '@/internal/utils/generate-url'
import { resolvePctLinkUrl } from '@/internal/utils/resolve-pctlink-url'
import { anchorFormatted } from '@/utils/format'

import * as styles from './structured-text-default.module.scss'

const StructuredTextDefault = ({
  content
}: {
  content: StructuredTextType | null
}) => {
  if (content === null) return <></>
  if (isEmptyDocument(content.value)) return <></>
  return (
    <div className={styles.content}>
      <StructuredText
        data={content}
        customNodeRules={[
          // Add HTML anchors to heading levels for in-page navigation
          renderNodeRule(isHeading, ({ node, children, key }) => {
            const HeadingTag = `h${node.level}` as keyof JSX.IntrinsicElements
            const nodeAsText = toPlainText(node) ?? ''
            const anchor = anchorFormatted(nodeAsText)
            return (
              <HeadingTag key={key} id={anchor}>
                {children}
              </HeadingTag>
            )
          }),
          renderNodeRule(isLink, ({ node, children, key }) => {
            const { url, meta } = node
            const openInNewWindow = meta?.some(
              (m) => m.id === 'target' && m.value === '_blank'
            )
            return (
              <Link to={url} key={key} openInNewWindow={openInNewWindow}>
                {children}
              </Link>
            )
          })
        ]}
        renderBlock={({ record }) => {
          switch (record.__typename) {
            case 'DatoCmsImage':
              return (
                <ImageInline
                  className={styles.image}
                  {...(record as Queries.ImageInlineFragment)}
                />
              )
            case 'DatoCmsVideo': {
              const block = record as Queries.VideoInlineFragment
              return block !== null ? (
                <div className={styles.player}>
                  <Player
                    autoPlay={!!block.preview?.url}
                    video={block.video?.url}
                    preview={block.preview?.url ?? null}
                    classes={{ wrapper: styles.wrapperPlayer }}
                  />
                </div>
              ) : (
                <></>
              )
            }
            case 'DatoCmsEmbed': {
              const block = record as Queries.EmbedInlineFragment
              return <Embed url={block.url} />
            }
            case 'DatoCmsForm': {
              const block = record as Queries.FormInlineFragment
              return <FormBlock {...block} />
            }
            case 'DatoCmsCta': {
              const block = record as unknown as Queries.CtaFragment
              return (
                <div className={styles.inlineRecord}>
                  <Cta {...block} />
                </div>
              )
            }
            default:
              return <></>
          }
        }}
        renderLinkToRecord={({ record, children, transformedMeta }) => {
          switch (record.__typename) {
            case 'DatoCmsPctDialog':
            case 'DatoCmsPctTopic':
            case 'DatoCmsPctPaper':
            case 'DatoCmsPctInternalPage':
            case 'DatoCmsPctPost':
            case 'DatoCmsPctProject':
            case 'DatoCmsPctEpisode':
            case 'DatoCmsPctPage':
            case 'DatoCmsPctVision':
            case 'DatoCmsPctTag':
            case 'DatoCmsGctPerson':
            case 'DatoCmsPctReport': {
              let openInNewWindow = false
              if (transformedMeta) {
                openInNewWindow = transformedMeta?.target === '_blank'
              }
              return (
                <Link
                  {...transformedMeta}
                  to={generateUrl(record.__typename, record.slug as string)}
                  openInNewWindow={openInNewWindow}
                >
                  {children}
                </Link>
              )
            }
            case 'DatoCmsPctLink': {
              const pctlink = record as Queries.PctLinkFragment
              const url = resolvePctLinkUrl(pctlink)
              return (
                <Link to={url} {...transformedMeta}>
                  {children}
                </Link>
              )
            }
            default:
              return <></>
          }
        }}
        renderInlineRecord={({ record }) => {
          switch (record.__typename) {
            case 'DatoCmsPctPaper': {
              const paperInline = record as Queries.PctPaperCardInlineFragment
              const data:
                | Queries.FormBlockFragment
                | Queries.FormInlineFragment = {
                __typename: 'DatoCmsForm',
                id: '',
                originalId: '',
                title: null,
                image: null,
                navigationLabel: null,
                formtype: formTypes.gate,
                gateContent: [
                  {
                    originalId: paperInline.id,
                    externalName: paperInline.externalName,
                    cover: paperInline.cover
                  }
                ]
              }
              return <FormBlock {...data} />
            }
            case 'DatoCmsPctDialog':
            case 'DatoCmsPctInternalPage':
            case 'DatoCmsPctPost':
            case 'DatoCmsPctEpisode':
            case 'DatoCmsPctProject':
            case 'DatoCmsPctPage':
            case 'DatoCmsPctVision':
            case 'DatoCmsPctInterview':
            case 'DatoCmsPctReport':
              return <CardInline record={record as unknown as CardsInline} />
            case 'DatoCmsPctLink': {
              const pctLink = record as Queries.PctLinkFragment
              return (
                <div className={styles.inlineRecord}>
                  <Button
                    to={resolvePctLinkUrl(pctLink)}
                    title={pctLink.externalName}
                    openNeWindow={pctLink.openInNewWindow ?? false}
                  />
                </div>
              )
            }
            case 'DatoCmsPctTopic': {
              const topic = record as Queries.PctTopicCardInlineFragment
              return (
                <div className={styles.inlineRecord}>
                  <Button
                    to={generateUrl(topic.__typename, topic.slug)}
                    title={topic.externalName}
                  />
                </div>
              )
            }
            case 'DatoCmsPctTag': {
              const tag = record as Queries.PctTagCardInlineFragment
              return (
                <div className={styles.inlineRecord}>
                  <Button
                    to={generateUrl(tag.__typename, tag.slug)}
                    title={tag.externalName}
                    color="secondary"
                  />
                </div>
              )
            }
            default:
              return <></>
          }
        }}
      />
    </div>
  )
}

export default StructuredTextDefault
